// SCR-STAFF-508 — تنظیمات بالینی (US-CLN-020، 021، 030، 061): قالب‌های یادداشت نسخه‌دار، عبارات سریع شخصی و مستأجر، فهرست تشخیص داخلی و قواعد
// منع مصرف (هدف، شرط، سطح، پیام؛ نمونه‌ها با نشان «نمونه» و غیرفعال تا اتصال به خدمات — C-AGENT-645). مدیریت قواعد فقط با cln.template.manage و
// cln.alert.override (C-AGENT-650).
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type ClnContraindicationLevel,
  type ClnContraindicationRuleView,
  type ClnDiagnosisCatalogView,
  type ClnNoteTemplateView,
  type ClnPhraseScope,
  type ClnQuickPhraseView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, ListPage, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { clnKeys, MaybeSample, SCR_STAFF_508 } from './common';

type TabKey = 'templates' | 'phrases' | 'rules' | 'catalog';

export function ClinicalSettingsPage() {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const full = access.accessMode === 'Full';
  const canManage = full && access.can('cln.template.manage');
  const canRules = canManage && access.can('cln.alert.override');
  const [tab, setTab] = useState<TabKey>('templates');
  const templates = useQuery({ queryKey: clnKeys.templates(tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/note-templates')) });
  const phrases = useQuery({ queryKey: clnKeys.phrases(tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/quick-phrases')) });
  const rules = useQuery({ queryKey: clnKeys.rules(tenantId), enabled: tab === 'rules', queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/contraindication-rules')) });
  const catalog = useQuery({
    queryKey: clnKeys.catalog(tenantId, '*'),
    enabled: tab === 'catalog',
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/diagnosis-catalog', { params: { query: { limit: 100, includeInactive: true } } })),
  });
  const services = useQuery({
    queryKey: ['clinical', tenantId, 'service-picker'],
    enabled: tab === 'rules' && canRules,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/services', { params: { query: { limit: 200, status: 'Active' } } })),
  });
  const [phrase, setPhrase] = useState<{ scope: ClnPhraseScope; shortcut: string; title: string; text: string } | null>(null);
  const [template, setTemplate] = useState<{ code: string; name: string; sections: string } | null>(null);
  const [rule, setRule] = useState<{ view: ClnContraindicationRuleView; serviceIds: string[]; isActive: boolean; level: ClnContraindicationLevel; message: string } | null>(null);
  const [item, setItem] = useState<{ code: string; titleFa: string; titleEn: string; isChronic: boolean } | null>(null);
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: clnKeys.all(tenantId) });

  const savePhrase = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/quick-phrases', {
          body: { scope: phrase?.scope ?? 'User', shortcut: phrase?.shortcut.trim() ?? '', title: phrase?.title.trim() || null, text: phrase?.text.trim() ?? '' },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setPhrase(null);
      invalidate();
    },
  });
  const deletePhrase = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.DELETE('/api/v1/clinical/quick-phrases/{id}', { params: { path: { id } } })),
    onSuccess: invalidate,
  });
  const saveTemplate = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/note-templates', {
          body: {
            code: template?.code.trim() ?? '',
            name: template?.name.trim() ?? '',
            specialtyCode: null,
            sections: (template?.sections ?? '')
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean)
              .map((line, index) => {
                const [sectionKey, ...title] = line.split(':');
                return { key: (sectionKey ?? `S${index}`).trim(), title: (title.join(':') || sectionKey || '').trim(), kind: 'FreeText' as const, prefill: 'None' as const, options: null, reference: null, required: false };
              }),
            serviceIds: [],
            isDefault: false,
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setTemplate(null);
      invalidate();
    },
  });
  const retire = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/clinical/note-templates/{id}:retire', { params: { path: { id } } })),
    onSuccess: invalidate,
  });
  const saveRule = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/clinical/contraindication-rules/{id}', {
          params: { path: { id: rule?.view.id ?? '' } },
          body: {
            code: null,
            targetLabel: rule?.view.targetLabel ?? null,
            serviceIds: rule?.serviceIds ?? [],
            categoryIds: rule?.view.categoryIds ?? [],
            condition: rule?.view.condition ?? { kind: 'Age' },
            level: rule?.level ?? 'Relative',
            message: rule?.message ?? '',
            isActive: rule?.isActive ?? false,
          },
        }),
      ),
    onSuccess: () => {
      setRule(null);
      invalidate();
    },
  });
  const saveItem = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/diagnosis-catalog', {
          body: { code: item?.code.trim() ?? '', titleFa: item?.titleFa.trim() ?? '', titleEn: item?.titleEn.trim() || null, isChronic: item?.isChronic ?? false, isActive: true },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setItem(null);
      invalidate();
    },
  });

  const templateColumns: DataTableColumn<ClnNoteTemplateView>[] = [
    { id: 'name', headerKey: 'cln:settings.name', cell: (row) => <span>{row.name} <MaybeSample isSample={row.isSample} /></span> },
    { id: 'code', headerKey: 'cln:settings.code', cell: (row) => <bdi dir="ltr">{`${row.code} v${row.version}`}</bdi> },
    { id: 'sections', headerKey: 'cln:settings.sections', cell: (row) => row.sections.map((section) => section.title).join(t('cln:common.list_separator')) },
    { id: 'status', headerKey: 'cln:common.status', cell: (row) => <StatusChip tone={row.status === 'Active' ? 'active' : 'inactive'} labelKey={`cln:template_status.${row.status}`} /> },
    {
      id: 'actions',
      headerKey: 'cln:common.actions',
      cell: (row) =>
        canManage && row.status === 'Active' && !row.isDefault ? (
          <Button size="small" color="inherit" onClick={() => retire.mutate(row.id)}>
            {t('cln:settings.retire')}
          </Button>
        ) : null,
    },
  ];
  const phraseColumns: DataTableColumn<ClnQuickPhraseView>[] = [
    { id: 'shortcut', headerKey: 'cln:settings.shortcut', cell: (row) => <bdi dir="ltr">/{row.shortcut}</bdi> },
    { id: 'text', headerKey: 'cln:settings.text', cell: (row) => <span>{row.text} <MaybeSample isSample={row.isSample} /></span> },
    { id: 'scope', headerKey: 'cln:settings.scope', cell: (row) => t(`cln:phrase_scope.${row.scope}`) },
    {
      id: 'actions',
      headerKey: 'cln:common.actions',
      cell: (row) =>
        full && row.canEdit ? (
          <Button size="small" color="inherit" onClick={() => deletePhrase.mutate(row.id)} data-testid="cln-phrase-delete">
            {t('cln:common.delete')}
          </Button>
        ) : null,
    },
  ];
  const ruleColumns: DataTableColumn<ClnContraindicationRuleView>[] = [
    { id: 'code', headerKey: 'cln:settings.code', cell: (row) => <span><bdi dir="ltr">{row.code}</bdi> <MaybeSample isSample={row.isSample} /></span> },
    { id: 'target', headerKey: 'cln:settings.target', cell: (row) => row.targetLabel ?? '—' },
    { id: 'condition', headerKey: 'cln:settings.condition', cell: (row) => t(`cln:condition_kind.${row.condition.kind}`) },
    { id: 'level', headerKey: 'cln:settings.level', cell: (row) => t(`cln:level.${row.level}`) },
    { id: 'message', headerKey: 'cln:settings.message', cell: (row) => row.message },
    { id: 'services', headerKey: 'cln:settings.services', cell: (row) => row.serviceIds.length + row.categoryIds.length },
    { id: 'active', headerKey: 'cln:common.status', cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} labelKey={row.isActive ? 'cln:settings.active' : 'cln:settings.inactive'} /> },
    {
      id: 'actions',
      headerKey: 'cln:common.actions',
      cell: (row) =>
        canRules ? (
          <Button size="small" onClick={() => setRule({ view: row, serviceIds: [...row.serviceIds], isActive: row.isActive, level: row.level, message: row.message })} data-testid="cln-rule-edit">
            {t('cln:common.edit')}
          </Button>
        ) : null,
    },
  ];
  const catalogColumns: DataTableColumn<ClnDiagnosisCatalogView>[] = [
    { id: 'code', headerKey: 'cln:settings.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'fa', headerKey: 'cln:settings.title_fa', cell: (row) => <span>{row.titleFa} <MaybeSample isSample={row.isSample} /></span> },
    { id: 'en', headerKey: 'cln:settings.title_en', cell: (row) => <bdi dir="ltr">{row.titleEn ?? '—'}</bdi> },
    { id: 'chronic', headerKey: 'cln:settings.chronic', cell: (row) => (row.isChronic ? t('cln:common.yes') : t('cln:common.no')) },
  ];

  const action =
    tab === 'phrases' && full ? (
      <Button variant="contained" onClick={() => setPhrase({ scope: canManage ? 'Tenant' : 'User', shortcut: '', title: '', text: '' })} data-testid="cln-phrase-add">
        {t('cln:settings.add_phrase')}
      </Button>
    ) : tab === 'templates' && canManage ? (
      <Button variant="contained" onClick={() => setTemplate({ code: '', name: '', sections: t('cln:settings.default_sections') })} data-testid="cln-template-add">
        {t('cln:settings.add_template')}
      </Button>
    ) : tab === 'catalog' && canManage ? (
      <Button variant="contained" onClick={() => setItem({ code: '', titleFa: '', titleEn: '', isChronic: false })} data-testid="cln-catalog-add">
        {t('cln:settings.add_diagnosis')}
      </Button>
    ) : undefined;

  return (
    <ListPage screenId={SCR_STAFF_508} titleKey="cln:settings.title" subtitleKey="cln:settings.subtitle" actions={action}>
      <Tabs value={tab} onChange={(_event, value: TabKey) => setTab(value)} sx={{ mb: 2 }} aria-label={t('cln:settings.title')}>
        <Tab value="templates" label={t('cln:settings.tabs.templates')} />
        <Tab value="phrases" label={t('cln:settings.tabs.phrases')} data-testid="cln-settings-phrases" />
        <Tab value="rules" label={t('cln:settings.tabs.rules')} data-testid="cln-settings-rules" />
        <Tab value="catalog" label={t('cln:settings.tabs.catalog')} data-testid="cln-settings-catalog" />
      </Tabs>
      {tab === 'templates' && (
        <DataTable captionKey="cln:settings.tabs.templates" columns={templateColumns} rows={templates.data?.items} getRowId={(row) => row.id} isLoading={templates.isPending} error={templates.error} emptyTitleKey="cln:settings.empty" />
      )}
      {tab === 'phrases' && (
        <DataTable captionKey="cln:settings.tabs.phrases" columns={phraseColumns} rows={phrases.data?.items} getRowId={(row) => row.id} isLoading={phrases.isPending} error={phrases.error} emptyTitleKey="cln:settings.empty" />
      )}
      {tab === 'rules' && (
        <DataTable captionKey="cln:settings.tabs.rules" columns={ruleColumns} rows={rules.data?.items} getRowId={(row) => row.id} isLoading={rules.isPending} error={rules.error} emptyTitleKey="cln:settings.empty" />
      )}
      {tab === 'catalog' && (
        <DataTable captionKey="cln:settings.tabs.catalog" columns={catalogColumns} rows={catalog.data?.items} getRowId={(row) => row.code} isLoading={catalog.isPending} error={catalog.error} emptyTitleKey="cln:settings.empty" />
      )}

      <FormDialog open={phrase !== null} titleKey="cln:settings.add_phrase" onSubmit={() => savePhrase.mutate()} onClose={() => setPhrase(null)} isDirty={Boolean(phrase?.text)} isSubmitting={savePhrase.isPending} submitError={savePhrase.error}>
        <TextField select label={t('cln:settings.scope')} value={phrase?.scope ?? 'User'} onChange={(event) => setPhrase((current) => (current ? { ...current, scope: event.target.value as ClnPhraseScope } : current))}>
          <MenuItem value="User">{t('cln:phrase_scope.User')}</MenuItem>
          {canManage && <MenuItem value="Tenant">{t('cln:phrase_scope.Tenant')}</MenuItem>}
        </TextField>
        <TextField label={t('cln:settings.shortcut')} required value={phrase?.shortcut ?? ''} onChange={(event) => setPhrase((current) => (current ? { ...current, shortcut: event.target.value } : current))} slotProps={{ htmlInput: { dir: 'ltr', maxLength: 20, 'data-testid': 'cln-phrase-shortcut' } }} />
        <TextField label={t('cln:settings.name')} value={phrase?.title ?? ''} onChange={(event) => setPhrase((current) => (current ? { ...current, title: event.target.value } : current))} />
        <TextField label={t('cln:settings.text')} required multiline minRows={3} value={phrase?.text ?? ''} onChange={(event) => setPhrase((current) => (current ? { ...current, text: event.target.value } : current))} slotProps={{ htmlInput: { maxLength: 4000, 'data-testid': 'cln-phrase-text' } }} />
      </FormDialog>

      <FormDialog open={template !== null} titleKey="cln:settings.add_template" onSubmit={() => saveTemplate.mutate()} onClose={() => setTemplate(null)} isDirty={Boolean(template?.name)} isSubmitting={saveTemplate.isPending} submitError={saveTemplate.error}>
        <TextField label={t('cln:settings.code')} required value={template?.code ?? ''} onChange={(event) => setTemplate((current) => (current ? { ...current, code: event.target.value } : current))} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'cln-template-code' } }} />
        <TextField label={t('cln:settings.name')} required value={template?.name ?? ''} onChange={(event) => setTemplate((current) => (current ? { ...current, name: event.target.value } : current))} slotProps={{ htmlInput: { 'data-testid': 'cln-template-name' } }} />
        <TextField label={t('cln:settings.sections')} helperText={t('cln:settings.sections_hint')} multiline minRows={4} value={template?.sections ?? ''} onChange={(event) => setTemplate((current) => (current ? { ...current, sections: event.target.value } : current))} />
      </FormDialog>

      <FormDialog open={rule !== null} titleKey="cln:settings.edit_rule" onSubmit={() => saveRule.mutate()} onClose={() => setRule(null)} isDirty={false} isSubmitting={saveRule.isPending} submitError={saveRule.error}>
        <TextField
          select
          label={t('cln:settings.services')}
          value={rule?.serviceIds ?? []}
          onChange={(event) => setRule((current) => (current ? { ...current, serviceIds: (typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value) as string[] } : current))}
          slotProps={{ select: { multiple: true }, htmlInput: { 'data-testid': 'cln-rule-services' } }}
        >
          {(services.data?.items ?? []).map((service) => (
            <MenuItem key={service.id} value={service.id}>
              {service.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField select label={t('cln:settings.level')} value={rule?.level ?? 'Relative'} onChange={(event) => setRule((current) => (current ? { ...current, level: event.target.value as ClnContraindicationLevel } : current))}>
          <MenuItem value="Absolute">{t('cln:level.Absolute')}</MenuItem>
          <MenuItem value="Relative">{t('cln:level.Relative')}</MenuItem>
        </TextField>
        <TextField label={t('cln:settings.message')} value={rule?.message ?? ''} onChange={(event) => setRule((current) => (current ? { ...current, message: event.target.value } : current))} />
        <FormControlLabel
          control={<Checkbox checked={rule?.isActive ?? false} onChange={(event) => setRule((current) => (current ? { ...current, isActive: event.target.checked } : current))} slotProps={{ input: { 'aria-label': t('cln:settings.active') } }} data-testid="cln-rule-active" />}
          label={t('cln:settings.active')}
        />
      </FormDialog>

      <FormDialog open={item !== null} titleKey="cln:settings.add_diagnosis" onSubmit={() => saveItem.mutate()} onClose={() => setItem(null)} isDirty={Boolean(item?.code)} isSubmitting={saveItem.isPending} submitError={saveItem.error}>
        <TextField label={t('cln:settings.code')} required value={item?.code ?? ''} onChange={(event) => setItem((current) => (current ? { ...current, code: event.target.value } : current))} slotProps={{ htmlInput: { dir: 'ltr' } }} />
        <TextField label={t('cln:settings.title_fa')} required value={item?.titleFa ?? ''} onChange={(event) => setItem((current) => (current ? { ...current, titleFa: event.target.value } : current))} />
        <TextField label={t('cln:settings.title_en')} value={item?.titleEn ?? ''} onChange={(event) => setItem((current) => (current ? { ...current, titleEn: event.target.value } : current))} slotProps={{ htmlInput: { dir: 'ltr' } }} />
        <FormControlLabel control={<Checkbox checked={item?.isChronic ?? false} onChange={(event) => setItem((current) => (current ? { ...current, isChronic: event.target.checked } : current))} />} label={t('cln:settings.chronic')} />
      </FormDialog>
    </ListPage>
  );
}
